import {Component, EventEmitter, HostBinding, OnDestroy, OnInit, Output} from '@angular/core';
import {EntityForm} from "@catuimodels/shared/entity-form";
import {SelectOption} from "@catuiform/types";
import {Project} from "@catcore/types/api-v2";
import {isNull, isNullOrUndefined} from "util";
import {Form, FormArray, FormBuilder, Validators} from "@angular/forms";
import {GenericResourceService} from "@catfeatures/services/generic-resource.service";
import {Router} from "@angular/router";
import {extractDataFromModel, sortByLabelFunc} from "@catcore/utils";
import {FeatureTypeService, ProjectService} from "@catcore/services";
import {EntitySchemaService} from "@catcore/services/entity-schema.service";
import {Observable, Observer} from "rxjs";
import {Service} from "@catmodels/units/types";

@Component({
  selector: 'catapp-ggw-dynamic-route-template-form',
  templateUrl: './ggw-dynamic-route-template-form.component.html',
  styleUrls: ['./ggw-dynamic-route-template-form.component.scss']
})
export class GgwDynamicRouteTemplateFormComponent extends EntityForm implements OnInit, OnDestroy {

  @Output() afterFormUpdate: EventEmitter<any> = new EventEmitter();

  private currentResourceService;
  private actionResourceService;

  private feature;
  private featureSubscription;
  private project;
  private projectSubscription;

  private ggwDeviceSubscription;

  connectFormArray: FormArray;
  disconnectFormArray: FormArray;

  sourceDeviceModelNames = '';
  sinkDeviceModelNames = '';

  private refreshRunning = false;
  private ggwDeviceOptions = [];

  get formIsReady() {
    return !isNullOrUndefined(this.form);
  }

  constructor(formBuilder: FormBuilder,
              private genericService: GenericResourceService,
              private projectService: ProjectService,
              private featureService: FeatureTypeService,
              private router: Router) {
    super(formBuilder);
    super.setUpdateIgnore(['connectActions', 'disconnectActions']);
  }

  ngOnInit() {
    if (this.isProjectLevel()) {
      this.projectSubscription = this.projectService.getCurrentProject()
        .subscribe((project) => {
          this.project = project;
        });
    } else {
      this.featureSubscription = this.featureService.getCurrent()
        .subscribe((feature) => {
          this.feature = feature;
        })
    }
  }

  ngOnDestroy() {
    if (this.ggwDeviceSubscription) {
      this.ggwDeviceSubscription.unsubscribe();
    }
    if (this.projectSubscription) {
      this.projectSubscription.unsubscribe();
    }
    if (this.featureSubscription) {
      this.featureSubscription.unsubscribe();
    }
  }

  /**
   * Refreshes the ggw device option list. Used by the sub-components for template action editing to lazy-load
   * the device list.
   */
  refreshDeviceOptions() {
    if (!this.refreshRunning && this.ggwDeviceOptions.length === 0
      && ((this.isProjectLevel() && !isNullOrUndefined(this.project.id))
          || (!(this.isProjectLevel()) && !isNullOrUndefined(this.feature)))) {
      this.refreshRunning = true;
      if (this.ggwDeviceSubscription) {
        this.ggwDeviceSubscription.unsubscribe();
      }
      if (this.isProjectLevel()) {
        this.ggwDeviceSubscription = this.projectService
          .getAllGgwDevices(this.project)
          .subscribe((ggwDevices) => {
            this.updateGgwDeviceOptions(ggwDevices);
          });
      } else {
        this.ggwDeviceSubscription = this.featureService
          .getGgwDevices(this.feature.id)
          .subscribe((ggwDevices) => {
            console.log('Found ggwDevices: ', ggwDevices);
            this.updateGgwDeviceOptions(ggwDevices);
          });
      }
    }
  }

  private updateGgwDeviceOptions(ggwDevices) {
    this.ggwDeviceOptions = [];
    ggwDevices.forEach((ggwDevice) => {
      this.ggwDeviceOptions.push({
        label: ggwDevice.label ? ggwDevice.label : ggwDevice.name,
        value: ggwDevice._links.self.href
      });
    });
    this.ggwDeviceOptions.sort(sortByLabelFunc);
    this.refreshRunning = false;
  }

  onEntityReceived(entity) {
    if (this.resourceUri) {
      this.currentResourceService = this.genericService.findServiceForResource(this.resourceUri);
    }

    if (this.hasModel(entity)) {
      this.sourceDeviceModelNames = entity._embedded.model._links.allowed_sources
        .map((res) => res.label)
        .join(', ');

      this.sinkDeviceModelNames = entity._embedded.model._links.allowed_sinks
        .map((res) => res.label)
        .join(', ');
    }
  }

  refreshResource() {
    if (this.currentResourceService) {
      this.currentResourceService.refresh();
    }
  }

  addConnectAction() {
    this.connectFormArray.push(this.buildActionFormControl({type: 'On Connect'}));
    this.updateSortOrder(this.connectFormArray);
  }

  addDisconnectAction() {
    this.disconnectFormArray.push(this.buildActionFormControl({type: 'On Disconnect'}));
    this.updateSortOrder(this.disconnectFormArray);
  }

  formSubmit($event) {
    const requests = [];

    // Template name
    const entityData = {
      id: this.form.value.id,
      name: this.form.value.name
    };
    requests.push(this.currentResourceService.save(entityData));

    // Deletes
    this.buildDeleteRequests().forEach((request) => {
      requests.push(request);
    });

    this.buildRequestsFromArray(this.connectFormArray, 'On Connect').forEach((request) => {
      requests.push(request);
    });
    this.buildRequestsFromArray(this.disconnectFormArray, 'On Disconnect').forEach((request) => {
      requests.push(request);
    });

    Observable.forkJoin(requests)
      .subscribe((multiple) => {
        console.log('Success');
        this.afterUpdateForm();
      });
  }

  protected buildDeleteRequests() {
    const requests = [];
    if (this.formData._links.actions && this.formData._links.actions.length > 0) {
      this.formData._links.actions.forEach((actionLink) => {
        console.log('Looking for:', actionLink.href);
        const existing = this.formContainsAction(actionLink.href);
        console.log('Existing:', existing);
        if (!existing) {
          console.log('Found! Deleting:', actionLink.href);
          requests.push(
            this.genericService.findServiceForResource(actionLink.href).delete()
          );
        }
      });
    }
    return requests;
  }

  protected formContainsAction(actionUri) {
    let found = false;
    this.connectFormArray.value.forEach((action) => {
      if (action.uri === actionUri) {
        found = true;
      }
    });

    this.disconnectFormArray.value.forEach((action) => {
      if (action.uri === actionUri) {
        found = true;
      }
    });

    return found;
  }

  protected buildRequestsFromArray(formArray: FormArray, type: string) {
    const requests = [];
    formArray.controls.forEach((actionFormGroup) => {
      if (actionFormGroup.dirty) {
        let action = actionFormGroup.value;
        let service = this.actionResourceService;
        let create = true;
        if (!isNullOrUndefined(action.id) && action.id !== '') {
          service = this.genericService.findServiceForResource(action.uri);
          create = false;
        }
        let newAction = action;
        delete newAction['id'];
        delete newAction['uri'];
        newAction['type'] = type;
        newAction['dynamic_route_template'] = this.formData._links.self.href;
        if (!isNullOrUndefined(action.device) && action.device !== '') {
          newAction.device_role = null;
        } else {
          newAction.device = null;
          if (create) {
            delete newAction['device'];
          }
        }
        requests.push(
          service.save(newAction, create)
        );
      }
    });
    return requests;
  }

  protected afterUpdateForm() {
    this.actionResourceService = this.genericService.findServiceForResource(this.schema.properties.actions.resourceUri);

    const connectFormArray = this.getFormBuilder().array([]);
    const disconnectFormArray = this.getFormBuilder().array([]);

    extractDataFromModel(this.formData, 'actions', '_embedded')
      .sort((a, b) => a.sort_order < b.sort_order ? -1 : (a.sort_order === b.sort_order ? 0 : 1))
      .forEach((action) => {
      const formControl = this.buildActionFormControl(action);

      if (action.type === 'On Connect') {
        connectFormArray.push(formControl);
      } else {
        disconnectFormArray.push(formControl);
      }
    });

    this.connectFormArray = connectFormArray;
    this.disconnectFormArray = disconnectFormArray;
    this.form.addControl('connectActions', this.connectFormArray);
    this.form.addControl('disconnectActions', this.disconnectFormArray);
    this.updateSortOrder(this.connectFormArray);
    this.updateSortOrder(this.disconnectFormArray);
    this.form.markAsPristine();

    this.afterFormSubmit();
  }

  protected afterDelete() {
    if (this.isProjectLevel()) {
      this.projectService.refreshCurrent();
      this.router.navigate(['projects', this.project.id, 'ggw', 'ggw-dynamic-route-templates']).then(() => {
      });
    } else {
      this.featureService.refreshCurrent();
      this.router.navigate(['features', this.feature.id]).then(() => {});
    }
  }

  protected afterFormSubmit() {
    if (this.isProjectLevel()) {
      //this.projectService.refreshCurrent();
    } else {
      this.featureService.refreshCurrent();
    }
  }

  private buildActionFormControl(action = null) {
    const formControl = this.getFormBuilder().group({
      id: [action.id ? action.id : '', []],
      uri: [action.id ? action._links.self.href : '', []],
      sort_order: [action.sort_order ? action.sort_order : '', []],
      type: [action.type ? action.type : '', []],
      device_role: [action.device_role ? action.device_role : '', []],
      device: [action._links && action._links.device ? action._links.device.href : '', []],
      action: [action.action ? action.action : '', [Validators.required]],
      value: [action.value ? action.value : '', []],
    });
    return formControl;
  }

  actionUp(formArray: FormArray, index: number) {
    if (index > 0) {
      let actionTmp = formArray.at(index);
      this.removeAction(formArray, index);
      formArray.insert(index - 1, actionTmp);
      formArray.markAsDirty();
      this.updateSortOrder(formArray);
    }
  }

  actionDown(formArray: FormArray, index: number) {
    if (index < formArray.length - 1) {
      let actionTmp = formArray.at(index);
      this.removeAction(formArray, index);
      formArray.insert(index + 1, actionTmp);
      formArray.markAsDirty();
      this.updateSortOrder(formArray);
    }
  }

  actionDelete(formArray: FormArray, index: number) {
    this.removeAction(formArray, index);
    this.updateSortOrder(formArray);
  }

  private removeAction(formArray: FormArray, index: number) {
    formArray.removeAt(index);
    formArray.markAsDirty();
    this.updateSortOrder(formArray);
  }

  private updateSortOrder(formArray: FormArray) {
    let i = 0;
    formArray.controls.forEach((control) => {
      let curSortOrder = control.value.sort_order;
      if (curSortOrder !== i) {
        control.patchValue({sort_order: i});
        control.markAsDirty();
      }
      i++;
    });
  }

  private hasModel(entity) {
    return entity._links && entity._links.model;
  }

  protected isProjectLevel() {
    return true;
  }
}




// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_develop@2/app/Resources/Public/src/app/modules/models/ggw-dynamic-routes/components/ggw-dynamic-route-template-form/ggw-dynamic-route-template-form.component.ts